// What Neo is checking while a specialist works inside its turn (TOM-543, cloud
// ADR 0341 §7, api_contracts.md §12.3). The stream's `agent` event says a
// specialist started or ended: `{ phase: 'start'|'end', agent, label, status? }`,
// value-free, sent only to a request that lists the `agents` capability. The busy
// row reads "Neo is checking your boards…" from the start until its end, or until
// the turn ends, whichever comes first. While Neo draws a picture (agent
// `picture`, TOM-646, cloud ADR 0387) it reads "Neo is drawing a picture…",
// and while it draws a page (agent `page`, TOM-871, cloud ADR 0399 §2) "Neo is
// drawing a page…".
//
// Pure, so cloud/web's vitest runs it under Node. NeoChat, the mate bar and the
// watch assistant fold each `agent` frame through `foldNeoAgentEvent` and clear
// the list when the turn ends.
//
// Value-free by construction: only the server's label reaches the screen, never
// the agent id, a status, a tool name or a reading. An agent id this client does
// not know still shows the label the server sent.

import type { NeoStreamEvent } from './neoStream';

/** The capability a chat request lists to receive `agent` events (TOM-543). */
export const NEO_AGENTS_CAPABILITY = 'agents';

/** The longest label the busy row shows; the server's are a few words. */
const LABEL_MAX = 60;

/** One decoded `agent` frame. */
export interface NeoAgentEvent {
  phase: 'start' | 'end';
  agent: string;
  label: string;
}

/** A specialist at work this turn, as the busy row names it. */
export interface NeoAgentAtWork {
  agent: string;
  label: string;
}

/** The label as one bounded line, or null when it names nothing. */
function oneLineLabel(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const flat = raw.replace(/\s+/g, ' ').trim();
  if (flat.length === 0) return null;
  return flat.length > LABEL_MAX ? `${flat.slice(0, LABEL_MAX - 1).trimEnd()}…` : flat;
}

/**
 * Decode one `agent` frame, defensively: a frame with an unknown phase or no
 * agent id is dropped. An `end` needs no label, since it only clears its start.
 */
export function parseNeoAgentEvent(data: NeoStreamEvent): NeoAgentEvent | null {
  const { phase, agent } = data;
  if (phase !== 'start' && phase !== 'end') return null;
  if (typeof agent !== 'string' || agent.trim().length === 0) return null;
  const label = oneLineLabel(data.label);
  if (phase === 'start' && label === null) return null;
  return { phase, agent: agent.trim(), label: label ?? '' };
}

/**
 * Fold one `agent` frame into the specialists at work. A start adds its agent
 * (a repeated start moves it to the end, the newest); an end removes it, and an
 * end with no start is a no-op. Anything that is not a valid `agent` frame
 * leaves the list as it was.
 */
export function foldNeoAgentEvent(
  atWork: readonly NeoAgentAtWork[],
  data: NeoStreamEvent
): readonly NeoAgentAtWork[] {
  const event = parseNeoAgentEvent(data);
  if (event === null) return atWork;
  const others = atWork.filter((a) => a.agent !== event.agent);
  if (event.phase === 'end') return others.length === atWork.length ? atWork : others;
  return [...others, { agent: event.agent, label: event.label }];
}

/**
 * What Neo is doing while an agent works, by its id: checking, unless the
 * agent makes something. Neo draws a picture (TOM-646) and a page (TOM-871);
 * it does not check one.
 */
const NEO_AGENT_VERBS: ReadonlyMap<string, string> = new Map([
  ['page', 'drawing'],
  ['picture', 'drawing'],
]);

/**
 * The busy row's line while a specialist works: the newest one's label, in
 * Neo's words ("Neo is checking your boards…", "Neo is drawing a picture…");
 * null when none is at work. `who` is the surface's name for Neo: the Alarms
 * page's mate bar says "The mate".
 */
export function neoAgentBusyLine(atWork: readonly NeoAgentAtWork[], who = 'Neo'): string | null {
  const newest = atWork.at(-1);
  if (newest === undefined) return null;
  const verb = NEO_AGENT_VERBS.get(newest.agent) ?? 'checking';
  return `${who} is ${verb} ${newest.label}…`;
}
